<template>
  <svg class="entry-scene" :class="[kind, { 'is-active': active }]" viewBox="0 0 360 164" fill="none" aria-hidden="true" focusable="false">
    <template v-if="kind === 'search'">
      <g class="scene-arrival search-query"><rect x="28" y="9" width="304" height="27" rx="3" class="paper" /><circle cx="43" cy="21" r="5" class="ink-line" /><path d="m47 25 3 3" class="ink-line" /><text x="60" y="26">Human-AI interaction</text></g>
      <g v-for="(label, i) in ['Research', 'Builds', 'Fragments']" :key="label" class="piece search-piece" :style="piece(searchPositions[i], [28, 45 + i * 36, 0])">
        <rect class="paper search-sheet" width="148" height="30" rx="2" />
        <rect v-if="i === 0" x="9" y="7" width="3" height="16" class="accent-fill" />
        <text x="20" y="19">{{ label }}</text><path d="M99 12h31m-31 6h20" class="soft-line search-idle-lines" />
        <text class="scene-arrival source-excerpt" x="106" y="19">{{ ['Learning and thinking with AI', 'Tools for working with knowledge', 'Thoughts, language and memory'][i] }}</text>
      </g>
      <g class="scene-departure search-lens"><circle cx="282" cy="65" r="23" class="ink-line" /><path d="m298 82 17 17" class="ink-line" /></g>
    </template>

    <template v-else-if="kind === 'ask'">
      <path d="M66 118 180 73l114 45" class="scene-arrival connection-line" pathLength="1" />
      <g class="scene-arrival answer"><rect x="34" y="10" width="215" height="28" rx="3" class="paper" /><text x="48" y="29">Your question</text><path d="M48 51h264v57H48z" class="paper" /><path d="M64 67h133m-133 12h223m-223 12h177" class="ink-line answer-lines" /><rect x="204" y="61" width="13" height="11" rx="2" class="accent-wash" /><text x="208" y="70" class="tiny">1</text><rect x="248" y="85" width="13" height="11" rx="2" class="accent-wash" /><text x="252" y="94" class="tiny">2</text></g>
      <g v-for="(label, i) in ['Research', 'Builds', 'Fragments']" :key="label" class="piece source-piece" :style="piece(askPositions[i], [28 + i * 106, 125, 0])">
        <rect width="94" height="27" rx="3" class="paper" /><circle cx="12" cy="13" r="2" class="accent-fill" /><text x="23" y="18">{{ label }}</text>
      </g>
      <g class="scene-departure ask-mark"><path d="M162 50h36v25h-23l-9 8v-8h-4z" class="paper" /><path d="M171 63h1m7 0h1m7 0h1" class="ink-line" /></g>
    </template>

    <template v-else-if="kind === 'view'">
      <path d="M226 47h24m-24 67h24" class="scene-arrival connection-line" pathLength="1" />
      <g v-for="(label, i) in ['Research in practice', 'Research', 'Builds']" :key="label" class="piece view-page" :class="`view-page-${i}`" :style="piece([132 + i * 5, 30 - i * 3, (i - 1) * 12], i === 0 ? [24, 10, 0] : [250, 10 + (i - 1) * 76, 0])">
        <rect width="94" height="117" rx="2" class="paper view-sheet" />
        <g class="scene-departure"><rect x="11" y="24" width="72" height="35" class="accent-wash" /><path d="M11 74h72m-72 9h56m-56 9h64" class="soft-line" /></g>
        <g class="scene-arrival">
          <text x="12" y="22">{{ label }}</text>
          <template v-if="i === 0"><rect x="12" y="35" width="60" height="40" class="accent-wash" /><path d="m20 62 12-14 13 9 16-12M84 43h104M84 53h88M84 63h96M12 88h176M12 98h153M12 108h167" class="soft-line" /><text x="12" y="131" class="view-caption">A focused reading path</text></template>
          <path v-else d="M12 34h57m-57 9h43m-43 9h50" class="soft-line" />
        </g>
      </g>
    </template>

    <template v-else-if="kind === 'cv'">
      <path d="M53 124H307" class="scene-arrival connection-line" pathLength="1" />
      <g v-for="(item, i) in cvItems" :key="item.label" class="piece cv-page" :style="piece([135 + i * 3, 25 + i * 2, (i - 1) * 8], [22 + i * 110, 13, 0])">
        <rect width="95" height="94" rx="2" class="paper" /><path d="M13 19h34m-34 14h69m-69 10h57m-57 10h62" class="soft-line" />
        <text class="scene-arrival" x="13" y="77">{{ item.label }}</text>
        <circle cx="47" cy="111" r="3" class="accent-fill scene-arrival" /><text x="47" y="139" text-anchor="middle" class="scene-arrival">{{ item.detail }}</text>
      </g>
    </template>

    <template v-else-if="kind === 'travels'">
      <g class="map-grid scene-arrival"><path d="M24 40h312M24 82h312M24 124h312M70 12v140m73-140v140m73-140v140m73-140v140" class="soft-line" /></g>
      <path d="M58 100Q104 2 170 59T300 101" class="travel-route scene-arrival connection-line" pathLength="1" />
      <g v-for="(place, i) in places" :key="place.label" class="piece map-place" :style="piece([155 + i * 4, 56 + i * 3, (i - 1) * 24], place.position)">
        <circle r="20" class="paper" /><path :d="['M-8 7V-7h6v14m4 0V-2h6v9', 'M-9 6h18M-5 4l5-12 5 12', 'M-9 6l7-11 5 6 6-9'][i]" class="ink-line" />
        <text y="39" text-anchor="middle" class="scene-arrival">{{ place.label }}</text>
      </g>
    </template>

    <template v-else-if="kind === 'traffic'">
      <g class="scene-arrival"><path d="M25 122h305M25 82h305M25 42h305" class="soft-line" /><text x="25" y="151">Visits over time</text><text x="329" y="151" text-anchor="end">Page activity</text></g>
      <g v-for="(height, i) in bars" :key="i" class="piece traffic-bar" :style="piece([128 + i * 12, 90 - (i % 3) * 13, 0], [30 + i * 34, 121 - height, 0])"><rect width="17" :height="active ? height : 5" rx="1" :class="i === 6 ? 'accent-fill' : 'bar-fill'" /></g>
      <path d="M38 94 72 71l34 8 34-28 34 13 34-35 34 10 34-22" class="scene-arrival traffic-trend ink-line" pathLength="1" />
    </template>
  </svg>
</template>

<script>
export default {
  name: 'HomeEntryScene',
  props: { kind: { type: String, required: true }, active: Boolean },
  data: () => ({
    searchPositions: [[25, 30, -7], [124, 76, 6], [42, 115, -3]],
    askPositions: [[28, 36, -8], [237, 43, 8], [131, 109, -4]],
    cvItems: [{ label: 'Study', detail: 'HKU' }, { label: 'Research', detail: 'SLR Lab' }, { label: 'Work', detail: 'PwC' }],
    places: [{ label: 'Hong Kong', position: [58, 100, 0] }, { label: 'Melbourne', position: [170, 59, 0] }, { label: "Hawke's Bay", position: [300, 101, 0] }],
    bars: [25, 44, 37, 65, 49, 83, 70, 103],
  }),
  methods: {
    piece(from, to) {
      return { '--rest': `translate(${from[0]}px, ${from[1]}px) rotate(${from[2]}deg)`, '--open': `translate(${to[0]}px, ${to[1]}px) rotate(${to[2]}deg)` };
    },
  },
};
</script>

<style scoped>
.entry-scene { display: block; width: 100%; height: 100%; overflow: visible; color: var(--ink, #343434); --scene-paper: #faf9f6; --scene-stroke: rgba(90, 83, 72, .23); --scene-accent: rgb(var(--site-accent-rgb)); --scene-ease: var(--entry-ease, cubic-bezier(.22, 1, .36, 1)); }
:global(body.dark-mode .entry-scene) { --scene-paper: #2d2c29; --scene-stroke: rgba(232, 223, 203, .24); }
.entry-scene text { fill: currentColor; font: 11px 'Quattrocento Sans', sans-serif; letter-spacing: .01em; }
.entry-scene .tiny { font-size: 8px; }
.paper { fill: var(--scene-paper); stroke: var(--scene-stroke); stroke-width: 1; }
.ink-line { stroke: currentColor; stroke-width: 1.2; stroke-linecap: round; stroke-linejoin: round; opacity: .65; }
.soft-line { stroke: currentColor; stroke-width: 1; opacity: .17; }
.accent-fill { fill: var(--scene-accent); }
.accent-wash { fill: var(--scene-accent); opacity: .13; }
.connection-line { stroke: var(--scene-accent); stroke-width: 1; stroke-dasharray: 1; stroke-dashoffset: 1; }
.is-active .connection-line { stroke-dashoffset: 0; }
.piece { transform: var(--rest); transition: transform var(--entry-duration, 520ms) var(--scene-ease); }
.is-active .piece { transform: var(--open); }
.travels:not(.is-active) .map-place { transform: var(--rest) scale(1.7); }
.scene-arrival { opacity: 0; transition: opacity 160ms ease, stroke-dashoffset var(--entry-duration, 560ms) var(--scene-ease); }
.is-active .scene-arrival { opacity: 1; transition-delay: 100ms, 0ms; }
.scene-departure { opacity: 1; transition: opacity 140ms ease, transform var(--entry-duration, 450ms) var(--scene-ease); }
.is-active .scene-departure { opacity: 0; transform: translate(8px, -8px); }
.search-sheet { transition: width var(--entry-duration, 520ms) var(--scene-ease); }
.is-active .search-sheet { width: 304px; }
.search-idle-lines { transition: opacity 120ms ease; }
.is-active .search-idle-lines { opacity: 0; }
.source-excerpt { font-size: 10px !important; opacity: 0; }
.is-active .source-excerpt { opacity: .66; }
.view-sheet { transition: width var(--entry-duration, 520ms) var(--scene-ease), height var(--entry-duration, 520ms) var(--scene-ease); }
.is-active .view-page-0 .view-sheet { width: 202px; height: 144px; }
.is-active .view-page-1 .view-sheet, .is-active .view-page-2 .view-sheet { width: 85px; height: 65px; }
.view-caption { font-size: 10px !important; opacity: .6; }
.traffic-bar rect { transition: height var(--entry-duration, 520ms) var(--scene-ease); }
.bar-fill { fill: currentColor; opacity: .18; }
.traffic-trend { stroke-dasharray: 1; stroke-dashoffset: 1; transition: stroke-dashoffset var(--entry-duration, 600ms) var(--scene-ease), opacity 180ms ease; }
.is-active .traffic-trend { stroke-dashoffset: 0; }
@media (prefers-reduced-motion: reduce) {
  .entry-scene *, .piece { transition: none !important; }
}
</style>
